import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Plug, Plus } from 'lucide-react';
import config from '../../config';
import googleAdsLogo from '../../logos/google-ads.png';

const API_URL = config.API_URL;

/**
 * Ce qu'affiche un outil Google Ads quand aucun compte ne lui est disponible.
 *
 * ## Ce qu'il remplace
 *
 * Dix outils affichaient « Aucun compte Google Ads trouvé », et rien d'autre : un constat sans
 * issue, au moment précis où l'utilisateur a besoin qu'on lui dise quoi faire. Le message était
 * recopié à l'identique dans chacun, ce qui garantissait que la correction serait oubliée
 * quelque part.
 *
 * ## Deux absences, deux gestes
 *
 * « Aucun compte » recouvre deux situations que l'ancien message confondait, alors qu'elles
 * n'appellent pas du tout la même action :
 *
 * | Situation | Ce qu'il faut faire |
 * |---|---|
 * | Aucun connecteur Google Ads | Autoriser Kokpit chez Google — on quitte l'application |
 * | Connecteur présent, aucun compte importé | Choisir ses comptes depuis l'écran Comptes |
 *
 * Proposer « Connecter Google Ads » à quelqu'un qui a déjà autorisé l'accès l'enverrait refaire
 * un consentement inutile, et ne résoudrait rien : ses comptes resteraient absents. D'où la
 * lecture des connecteurs avant de choisir le bouton.
 *
 * ## Le cas de l'accès client
 *
 * Un rôle `CLIENT` ne branche pas de connecteur : il consulte ceux de son agence. Lui proposer
 * de connecter quoi que ce soit l'enverrait vers un écran qui lui est fermé — on se contente
 * alors de dire que rien ne lui a été ouvert.
 */
function AucunCompteGoogleAds({ token, role, message = 'Aucun compte Google Ads trouvé' }) {
  const [connecteurs, setConnecteurs] = useState(null);

  useEffect(() => {
    if (!token) return undefined;
    let annule = false;
    (async () => {
      try {
        const { data } = await axios.get(`${API_URL}/api/connectors`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (!annule) setConnecteurs(data?.connectors || []);
      } catch (e) {
        // Injoignable : on retombe sur la proposition de connexion, qui est le cas le plus
        // fréquent et le moins coûteux s'il est faux — on y voit ses connecteurs existants.
        if (!annule) setConnecteurs([]);
      }
    })();
    return () => { annule = true; };
  }, [token]);

  const estClient = role === 'CLIENT';
  const aLeConnecteur = Array.isArray(connecteurs)
    && connecteurs.some((c) => c.provider === 'GOOGLE_ADS' && c.isActive !== false);

  const connecter = () => {
    window.location.href = `${API_URL}/api/auth/google-ads?token=${token}&returnTo=connectors`;
  };

  const ouvrirLesComptes = () => {
    // Les outils ne reçoivent pas tous de quoi changer d'écran : on le demande à l'application,
    // qui seule tient la vue courante. Voir l'écouteur `kokpit:naviguer` dans App.js.
    window.dispatchEvent(new CustomEvent('kokpit:naviguer', { detail: { vue: 'library' } }));
  };

  return (
    <div style={styles.cadre}>
      <img src={googleAdsLogo} alt="" style={{ width: '40px', height: '40px', marginBottom: '16px' }} />

      <div style={{ fontSize: '18px', fontWeight: 600, color: '#374151', marginBottom: '6px' }}>
        {message}
      </div>
      <div style={{ fontSize: '15px', color: '#6b7280', marginBottom: '20px' }}>
        {estClient
          ? "Aucun compte Google Ads ne vous a été ouvert par votre agence."
          : aLeConnecteur
            ? "Votre compte Google est connecté, mais aucun compte publicitaire n'a encore été ajouté."
            : "Connectez votre compte Google Ads pour utiliser cet outil."}
      </div>

      {/* Tant que les connecteurs ne sont pas lus, aucun bouton : en proposer un au hasard
          enverrait une personne sur deux au mauvais endroit. */}
      {!estClient && connecteurs !== null && (
        aLeConnecteur ? (
          <button style={styles.bouton} onClick={ouvrirLesComptes}>
            <Plus size={16} />
            Ajouter un compte
          </button>
        ) : (
          <button style={styles.bouton} onClick={connecter}>
            <Plug size={16} />
            Connecter Google Ads
          </button>
        )
      )}
    </div>
  );
}

const styles = {
  /**
   * Le cadre de l'état vide, repris de `.diagnostic-empty` (DiagnosticView.css).
   *
   * Porté ici plutôt que laissé à chaque appelant : les dix outils héritaient sinon du
   * conteneur qui les entoure — un filet de vingt pixels pour les uns, rien pour les autres —
   * et le même état se présentait de dix façons. En l'inscrivant dans le composant, un outil
   * qui l'adopte obtient la mise en page avec, sans avoir à la connaître.
   *
   * Les valeurs sont recopiées plutôt qu'importées : la feuille de style du diagnostic est
   * chargée par son écran, et un composant partagé qui dépendrait d'elle s'afficherait nu
   * partout ailleurs.
   */
  cadre: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '64px',
    background: '#f9fafb',
    borderRadius: '16px',
    border: '2px dashed #e5e7eb',
    textAlign: 'center'
  },
  bouton: {
    display: 'inline-flex', alignItems: 'center', gap: '8px',
    padding: '10px 18px', background: '#1a1a1a', color: 'white',
    border: 'none', borderRadius: '8px', fontSize: '14px', fontWeight: 600, cursor: 'pointer'
  }
};

export default AucunCompteGoogleAds;
